import React, { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import Cookies from 'js-cookie'
import API_BASE_URL from '../../../config/api'
import Topbar from '../mini-comp/Topbar'
import Sidebar from '../mini-comp/Sidebar'
import { Link, useSearchParams } from 'react-router-dom'
import { RiEdit2Fill, RiDeleteBinLine, RiEyeLine, RiEditLine, RiUserLine, RiFilePdfLine, RiSearchLine } from 'react-icons/ri'
import Swal from 'sweetalert2'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import ImagePreviewModal from '../mini-comp/ImagePreviewModal'

const AllEmployees = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [subscribedModules, setSubscribedModules] = useState([]);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState({ url: '', title: '' });
    const [nameFilter, setNameFilter] = useState('');

    const hasModule = (moduleName) => {
        return subscribedModules.some(m =>
            (m.name || m.module_name || '').toLowerCase().includes(moduleName.toLowerCase())
        )
    }
    const isRestricted = hasModule('Payroll') || hasModule('Leave');

    const getStatusBadge = (status) => {
        const badges = {
            active: 'bg-label-success',
            terminated: 'bg-label-danger',
            on_leave: 'bg-label-warning',
            suspended: 'bg-label-info'
        };
        return badges[status?.toLowerCase()] || 'bg-label-secondary';
    };

    const getStatusLabel = (status) => {
        const labels = {
            active: 'Active',
            terminated: 'Terminated',
            on_leave: 'On Leave',
            suspended: 'Suspended'
        };
        return labels[status?.toLowerCase()] || 'N/A';
    };

    const employmentStatusFilter = searchParams.get('employment_status') || '';

    const normalizeName = (value) => (value || '').toLowerCase().trim().replace(/\s+/g, ' ');

    const filteredEmployees = useMemo(() => {
        const query = normalizeName(nameFilter);
        if (!query) return employees;

        return employees.filter((employee) => {
            const firstName = normalizeName(employee.first_name);
            const lastName = normalizeName(employee.last_name);
            const fullName = normalizeName(`${employee.first_name || ''} ${employee.last_name || ''}`);

            return firstName.includes(query) || lastName.includes(query) || fullName.includes(query);
        });
    }, [employees, nameFilter]);

    const handleStatusFilterChange = (status) => {
        if (status) {
            searchParams.set('employment_status', status);
        } else {
            searchParams.delete('employment_status');
        }
        setSearchParams(searchParams);
    };

    const fetchEmployees = async (employmentStatus = '') => {
        try {
            setLoading(true);
            const token = Cookies.get('token');
            const params = {};
            if (employmentStatus) {
                params.employment_status = employmentStatus;
            }
            const response = await axios.get(`${API_BASE_URL}/api/v1/employees/`, {
                headers: { 'Authorization': `Bearer ${token}` },
                params
            });
            setEmployees(Array.isArray(response.data) ? response.data : []);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching employees:', error);
            setLoading(false);
            Swal.fire('Error', 'Failed to fetch employees.', 'error');
        }
    };

    const handleImageClick = (url, name) => {
        if (!url) return;
        setSelectedImage({ url, title: name });
        setPreviewModalOpen(true);
    };

    useEffect(() => {
        fetchEmployees(employmentStatusFilter);
    }, [employmentStatusFilter]);

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const token = Cookies.get('token');
                await axios.delete(`${API_BASE_URL}/api/v1/employees/${id}/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                Swal.fire('Deleted!', 'Employee has been deleted.', 'success');
                fetchEmployees(employmentStatusFilter);
            } catch (error) {
                console.error('Error deleting employee:', error);
                Swal.fire('Error', 'Failed to delete employee.', 'error');
            }
        }
    };

    const fileInputRef = React.useRef(null);

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            setLoading(true);
            const token = Cookies.get('token');
            await axios.post(`${API_BASE_URL}/api/v1/employees/bulk-import/`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            Swal.fire('Success', 'Employees imported successfully', 'success');
            fetchEmployees(employmentStatusFilter);
        } catch (error) {
            console.error('Error importing employees:', error);
            Swal.fire('Error', error.response?.data?.error || 'Failed to import employees.', 'error');
        } finally {
            setLoading(false);
            event.target.value = ''; // Reset input
        }
    };

    const handleExportPDF = () => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4'
        });

        const tableColumn = ["Employee Name", "Email", "Phone", "Position", "Department", "Status"];
        const tableRows = [];

        filteredEmployees.forEach(employee => {
            const employeeData = [
                `${employee.first_name} ${employee.last_name}`,
                employee.email,
                employee.phone_number || 'N/A',
                employee.job_title || employee.position || 'N/A',
                employee.department_name || (typeof employee.department === 'object' ? employee.department?.name : employee.department) || 'N/A',
                employee.employment_status ? getStatusLabel(employee.employment_status) : 'N/A'
            ];
            tableRows.push(employeeData);
        });

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20,
            theme: 'grid',
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillStyle: '#5965efff', textColor: '#ffffff' },
            alternateRowStyles: { fillStyle: '#f2f2f2' },
            margin: { top: 20 }
        });

        doc.text("Employee List", 14, 15);
        doc.save("employees_list.pdf");
    };

    return (
        <div>
            <Topbar />
            <Sidebar />

            <div className="app-content-area">
                <div className="app-content-wrap">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-xl-12">
                                <div className="card">
                                    <div className="card-header justify-between">
                                        <h4 className="d-flex-items gap-10">Employees <span className="badge bg-label-primary">{filteredEmployees.length}</span></h4>
                                        <div className="d-flex flex-wrap gap-15">
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                style={{ display: 'none' }}
                                                onChange={handleFileUpload}
                                                accept=".xlsx, .xls"
                                            />
                                            <button
                                                className="btn btn-success text-white"
                                                onClick={() => fileInputRef.current.click()}
                                            >
                                                Import Excel
                                            </button>
                                            <Link to="/hr/create-employee" type="button" className="btn btn-primary">
                                                Add Employee
                                            </Link>
                                        
                                            {hasModule('HR Admin') && (
                                                <button className="btn btn-danger text-white d-flex align-items-center gap-5" onClick={handleExportPDF}>
                                                    <RiFilePdfLine /> Export As PDF
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="card-body pt-15">
                                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-10 mb-20">
                                            <div className="d-flex flex-wrap gap-10">
                                            <button
                                                type="button"
                                                className={`btn btn-sm px-3 ${employmentStatusFilter === '' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => handleStatusFilterChange('')}
                                            >
                                                All
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm px-3 ${employmentStatusFilter === 'active' ? 'btn-success' : 'btn-outline-success'}`}
                                                onClick={() => handleStatusFilterChange('active')}
                                            >
                                                Active
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm px-3 ${employmentStatusFilter === 'terminated' ? 'btn-danger' : 'btn-outline-danger'}`}
                                                onClick={() => handleStatusFilterChange('terminated')}
                                            >
                                                Terminated
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm px-3 ${employmentStatusFilter === 'suspended' ? 'btn-info' : 'btn-outline-info'}`}
                                                onClick={() => handleStatusFilterChange('suspended')}
                                            >
                                                Suspended
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm px-3 ${employmentStatusFilter === 'on_leave' ? 'btn-warning' : 'btn-outline-warning'}`}
                                                onClick={() => handleStatusFilterChange('on_leave')}
                                            >
                                                On Leave
                                            </button>
                                            </div>
                                            <div className="search-input-wrapper position-relative" style={{ minWidth: '240px', maxWidth: '320px', flex: '1 1 240px' }}>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm ps-30"
                                                    placeholder="Search by first or full name..."
                                                    value={nameFilter}
                                                    onChange={(e) => setNameFilter(e.target.value)}
                                                    aria-label="Filter employees by name"
                                                />
                                            </div>
                                        </div>
                                        <div className="table-responsive">
                                            {loading ? (
                                                <div className="text-center py-5">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <table id="dataTableDefault" className="table text-nowrap w-100">
                                                    <thead>
                                                        <tr>
                                                            <th>Employee Name</th>
                                                            <th>Email</th>
                                                            {isRestricted && <th>Phone</th>}
                                                            <th>Position</th>
                                                            <th>Department</th>
                                                            <th>Status</th>
                                                            <th>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {filteredEmployees.length > 0 ? (
                                                            filteredEmployees.map((employee) => (
                                                                <tr key={employee.id}>
                                                                    <td className="d-flex-items gap-10">
                                                                        <div 
                                                                            className="avatar radius-100"
                                                                            style={{ cursor: employee.photo ? 'pointer' : 'default' }}
                                                                            onClick={() => employee.photo && handleImageClick(`${API_BASE_URL}${employee.photo}`, `${employee.first_name} ${employee.last_name}`)}
                                                                            title={employee.photo ? "Click to view full image" : ""}
                                                                        >
                                                                            {employee.photo ? (
                                                                                <img className="radius-100" src={`${API_BASE_URL}${employee.photo}`} alt={`${employee.first_name}`} />
                                                                            ) : (
                                                                                <div className="radius-100 bg-light d-flex align-items-center justify-content-center h-100 w-100 border">
                                                                                    <RiUserLine size={20} className="text-primary" />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <h6>
                                                                            <a href={`/hr/employee-details/${employee.id}`}>{employee.first_name} {employee.last_name}</a>
                                                                        </h6>
                                                                    </td>
                                                                    <td><a href={`mailto:${employee.email}`}>{employee.email}</a></td>
                                                                    {isRestricted && <td>{employee.phone_number || 'N/A'}</td>}
                                                                    <td>{employee.job_title || employee.position || 'N/A'}</td>
                                                                    <td>
                                                                        {employee.department_name ||
                                                                            (typeof employee.department === 'object' ? employee.department?.name : employee.department) ||
                                                                            'N/A'}
                                                                    </td>
                                                                    <td>
                                                                        <span className={`badge ${getStatusBadge(employee.employment_status)}`}>
                                                                            {getStatusLabel(employee.employment_status)}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <div className="d-flex-items gap-5">
                                                                            <a className="btn-icon btn-success-light" href={`/hr/employee/${employee.id}`}>
                                                                                <RiEyeLine />
                                                                            </a>
                                                                            <a className="btn-icon btn-info-light" href={`/hr/edit-employee/${employee.id}`}>
                                                                                <RiEditLine />
                                                                            </a>
                                                                            <button className="btn-icon btn-danger-light removeRow" type="button" onClick={() => handleDelete(employee.id)}>
                                                                                <RiDeleteBinLine />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="7" className="text-center">
                                                                    {nameFilter.trim()
                                                                        ? `No employees matching "${nameFilter.trim()}".`
                                                                        : 'No employees found.'}
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ImagePreviewModal 
                isOpen={previewModalOpen}
                imageUrl={selectedImage.url}
                title={selectedImage.title}
                onClose={() => setPreviewModalOpen(false)}
            />
        </div>
    )
}

export default AllEmployees

